/**
 * Offline Queue Store - IndexedDB-based mutation queue
 * 
 * Stores queued mutations (insert/update/delete) in IndexedDB for sync
 * when connectivity returns. This replaces localStorage-based queue
 * to ensure health data is NOT stored in localStorage.
 */

import { logger } from '@/lib/logger';

const DB_NAME = 'lupuslogix-offline-queue';
const DB_VERSION = 1;
const STORE_NAME = 'queue';

export interface QueuedAction {
  id: string;
  table: string;
  action: 'insert' | 'update' | 'delete' | 'upsert';
  data: Record<string, unknown>;
  timestamp: number;
  retryCount: number;
  onConflict?: string;
}

class OfflineQueueStore {
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Initialize the IndexedDB database
   */
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        logger.error('[OfflineQueueStore] Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('table', 'table', { unique: false });
        }
      };
    });

    return this.dbPromise;
  }

  /**
   * Get all queued actions
   */
  async getAll(): Promise<QueuedAction[]> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('[OfflineQueueStore] Failed to get queue:', error);
      return [];
    }
  }

  /**
   * Add an action to the queue
   */
  async add(action: QueuedAction): Promise<void> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.put(action);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      if (import.meta.env.DEV) {
        logger.log('[OfflineQueueStore] Action queued:', action.id);
      }
    } catch (error) {
      logger.error('[OfflineQueueStore] Failed to add action:', error);
    }
  }

  /**
   * Update an action in the queue (e.g., increment retry count)
   */
  async update(action: QueuedAction): Promise<void> {
    return this.add(action); // put() handles both insert and update
  }

  /**
   * Remove an action from the queue
   */
  async remove(id: string): Promise<void> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      if (import.meta.env.DEV) {
        logger.log('[OfflineQueueStore] Action removed:', id);
      }
    } catch (error) {
      logger.error('[OfflineQueueStore] Failed to remove action:', error);
    }
  }

  /**
   * Clear all actions from the queue
   */
  async clear(): Promise<void> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      if (import.meta.env.DEV) {
        logger.log('[OfflineQueueStore] Queue cleared');
      }
    } catch (error) {
      logger.error('[OfflineQueueStore] Failed to clear queue:', error);
    }
  }

  /**
   * Get the number of queued actions
   */
  async count(): Promise<number> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('[OfflineQueueStore] Failed to count queue:', error);
      return 0;
    }
  }
}

export const offlineQueueStore = new OfflineQueueStore();
